import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { AppInfo } from '@capacitor/app';
import { FileInfo } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

import { Profile, ProfileResponse, ProfileUpdateRequest } from '#models/db/profile/profile.model';
import { IntermediaryService, MS_DELAY } from '#services/intermediary/intermediary.service';
import { ApiRepositoryService } from '#repositories/api-repository/api-repository.service';
import { IProfileService } from './profile.interface';
import { logger } from '#lib/logger';
import { FileSystemService } from '#services/file-system/file-system.service';
import { BucketName } from '#models/db/asset/asset.model';
import { ResetService } from '#services/reset/reset.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileService implements IProfileService {
  path = 'profile';

  getProfileLoading$ = new Subject<boolean>();
  updateProfileLoading$ = new Subject<boolean>();
  deleteProfileLoading$ = new Subject<boolean>();
  switchProfileLoading$ = new Subject<boolean>();

  profile$ = new BehaviorSubject<Profile | null>(null);

  appInfo$ = new BehaviorSubject<{ version: string; build: string } | null>(null);

  constructor(
    private apiRepository: ApiRepositoryService,
    private intermediaryHandler: IntermediaryService,
    private fileSystemService: FileSystemService,
    private resetService: ResetService
  ) {}

  getProfile(nextFn?: (data: Profile | null) => void, silent?: boolean, switchProfile?: boolean) {
    const loading = switchProfile ? this.switchProfileLoading$ : this.getProfileLoading$;

    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? loading : undefined,
        subject: this.profile$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<ProfileResponse>({ url: `${this.path}` });
        const designated_contact_permissions =
          response.details?.designated_contact_permissions.filter(
            (permission) => permission.status === 'Accepted'
          ) || [];

        const details = response.details;

        logger.log(details, 'ProfileService.getProfile -> response');

        const path = details?.profile_image_path ?? '';
        const profileBucket: BucketName = 'profile-picture-uploads';

        if (path && path.includes(profileBucket)) {
          let localFileExists: FileInfo | null = null;
          const filePath = path.substring(path.indexOf(profileBucket));

          // Attempt to read the file from the local file system if on a native platform.
          if (Capacitor.isNativePlatform()) {
            // Check if the file exists locally first
            localFileExists = await this.fileSystemService.fileExists(filePath);

            if (localFileExists) {
              // Read the file as a blob and create a blob URL
              const fileBlob = await this.fileSystemService.readFileAsBlob(localFileExists.uri);

              if (fileBlob) {
                const blobUrl = URL.createObjectURL(fileBlob);

                details.profile_image_path = blobUrl;
                // Store reference to revoke later if needed
                details.local_blob_url = blobUrl;
              }
            }
          }

          if (!localFileExists && Capacitor.isNativePlatform()) {
            // If the file does not exist locally, save it to the file system
            await this.fileSystemService.saveFile(details.profile_image_path!, filePath);
          }
        }

        // Only send back permissions that are accepted.
        const data: Profile = {
          ...details,
          designated_contact_permissions,
        };

        return data;
      }
    );
  }

  updateProfile(
    request: Partial<ProfileUpdateRequest>,
    nextFn?: (data: Profile | null) => void,
    silent?: boolean
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.updateProfileLoading$ : undefined,
        subject: this.profile$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.patch<
          ProfileResponse,
          Partial<ProfileUpdateRequest>
        >({ url: `${this.path}/${request.id}` }, request);

        const data: Profile = {
          ...response.details,
          designated_contact_permissions: this.profile$.value?.designated_contact_permissions || [],
        };

        logger.log(data, 'ProfileService.updateProfile -> response');

        return data;
      }
    );
  }

  deleteProfile(id: string, nextFn?: () => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.deleteProfileLoading$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        await this.apiRepository.delete({ url: `${this.path}/${id}` });

        logger.log(id, 'ProfileService.deleteProfile -> response');

        return;
      }
    );
  }

  setAppInfo(appInfo: AppInfo) {
    const { version, build } = appInfo;

    this.appInfo$.next({ version, build });
    logger.log({ version, build }, 'ProfileService.setAppInfo');
  }

  /**
   * Resets the profile service and all associated data across the application. This is
   * called when a user logs out to ensure that all personal data is cleared from the app.
   *
   * This method performs the following operations:
   * 1. Revokes any existing blob URL associated with the current profile to prevent memory leaks
   * 2. Clears the current profile by setting it to null
   * 3. Logs the completion of the reset operation
   * 4. Triggers a reset of all data across the application via the reset service
   */
  reset() {
    const currentProfile = this.profile$.value;

    if (currentProfile?.local_blob_url) {
      // Revoke the blob URL to free up memory
      URL.revokeObjectURL(currentProfile.local_blob_url);
    }

    this.profile$.next(null);
    logger.log('complete', 'ProfileService.resetProfile');

    this.resetService.resetAllData();
  }
}
